// KA Intelligence — isolated public beta.
//
// Deliberately not wired into /ask or any existing surface. One column, one
// input, no personality picker theatre: depth is chosen for the visitor, with
// an optional manual override for people who want it.
//
// Two hard laws live in this file:
//   1. No reasoning / chain-of-thought is ever rendered. The server does not
//      send it; this component has no code path that could display it.
//   2. Conversation memory is LOCAL ONLY — localStorage, guest-safe, bounded,
//      never uploaded and never persisted server-side.

import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import {
  Loader2,
  MapPin,
  Plus,
  Send,
  Sparkles,
  Square,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { ROLE_LABELS } from "@/lib/intelligence/router";
import { rateIntelligenceRun } from "@/lib/intelligence.functions";
import { describePublicPath, sanitizePublicPath } from "@/lib/intelligence/page-context";
import { groupSources, type AnswerMetadata, type AnswerSource } from "@/lib/intelligence/sources";
import {
  clearLocalConversation,
  loadLocalConversation,
  saveLocalConversation,
  type LocalMessage,
} from "@/lib/intelligence/local-memory";
import { VoiceControls } from "@/components/intelligence/VoiceControls";
import { ActionConfirmCard, AgentTraceSummary } from "@/components/intelligence/ActionConfirmCard";

import { connectionState, publicErrorMessage } from "@/lib/intelligence/public-error";
import { withStallGuard } from "@/lib/intelligence/stall-guard";
import { CHAMBER_BOTTOM_RESERVE } from "@/lib/chrome-policy";

export const Route = createFileRoute("/intelligence")({
  validateSearch: (search: Record<string, unknown>) => ({
    from: typeof search["from"] === "string" ? search["from"] : undefined,
  }),
  head: () => ({
    meta: [
      { title: "KA Intelligence — Ask the Eternal Buddha Mind | KAfrequency" },
      {
        name: "description",
        content:
          "KA Intelligence is the KAfrequency reasoning system: grounded answers on Buddhism, Kemetic spiritual science, the Codex, music and practice. Public beta.",
      },
      { property: "og:title", content: "KA Intelligence — Ask the Eternal Buddha Mind" },
      {
        property: "og:description",
        content:
          "A grounded, source-disciplined intelligence for Buddhist and Kemetic study, the 47 Gates Codex, and the Buddha KA catalogue. Public beta.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      // Beta isolation: the surface stays out of the index while links are
      // still followed, so it cannot compete with canonical authority pages.
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: IntelligencePage,
});

const ROLE_OPTIONS = [
  { value: "auto", label: "Auto" },
  { value: "guide", label: "Guide" },
  { value: "scholar", label: "Scholar" },
  { value: "creator", label: "Creator" },
  { value: "strategist", label: "Strategist" },
  { value: "deep_temple", label: "Deep Temple" },
] as const;

const OPENERS = [
  "What is KAfrequency, in one paragraph?",
  "Explain Ma'at next to the Eightfold Path.",
  "Where should I begin in the Codex?",
  "Give me a five-minute practice for anger.",
];

function textOf(m: UIMessage): string {
  return m.parts.map((p) => (p.type === "text" ? p.text : "")).join("");
}

function sourcesOf(m: UIMessage): AnswerSource[] {
  const meta = m.metadata as AnswerMetadata | undefined;
  return Array.isArray(meta?.sources) ? meta!.sources! : [];
}

function metaOf(m: UIMessage): AnswerMetadata | undefined {
  return m.metadata as AnswerMetadata | undefined;
}

/** Rehydrate local-only history into UIMessages the chat can display. */
function toUIMessages(list: LocalMessage[]): UIMessage[] {
  return list.map((m) => ({
    id: m.id,
    role: m.role,
    parts: [{ type: "text" as const, text: m.text }],
  }));
}

function SourceChips({ sources }: { sources: AnswerSource[] }) {
  if (sources.length === 0) return null;
  const groups = groupSources(sources);
  return (
    <div className="mt-3 border-t border-border/40 pt-3">
      <h3 className="text-[11px] uppercase tracking-wider text-muted-foreground">Sources</h3>
      <div className="mt-2 space-y-2">
        {groups.map((g) => (
          <div key={g.group}>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70">{g.group}</p>
            <ul className="mt-1 flex flex-wrap gap-1.5">
              {g.sources.map((s) => (
                <li key={s.url}>
                  <a
                    href={s.url}
                    target={g.group === "Current Web" ? "_blank" : undefined}
                    rel={g.group === "Current Web" ? "noopener noreferrer nofollow" : undefined}
                    className="inline-block max-w-[16rem] truncate rounded-full border border-border/60 px-2.5 py-1 text-[11px] text-muted-foreground transition hover:border-gold/50 hover:text-gold"
                    title={s.title}
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function IntelligencePage() {
  const search = useSearch({ from: "/intelligence" });
  const [input, setInput] = useState("");
  const [roleChoice, setRoleChoice] = useState<string>("auto");
  const [activeRole, setActiveRole] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [rated, setRated] = useState<number | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [restored, setRestored] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // `?from=` is untrusted: only a same-site public path is ever accepted.
  const contextPath = useMemo(() => sanitizePublicPath(search.from ?? null), [search.from]);

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: "/api/intelligence",
        headers: async (): Promise<Record<string, string>> => {
          const { data } = await supabase.auth.getSession();
          const token = data.session?.access_token;
          return token ? { Authorization: `Bearer ${token}` } : {};
        },
        fetch: async (url, init) => {
          const res = await fetch(url as string, init);
          const id = res.headers.get("X-KA-Run-Id");
          const role = res.headers.get("X-KA-Role");
          if (id) setRunId(id);
          if (role) setActiveRole(role);
          setRated(null);
          // Never fail silently: a non-2xx answer carries a safe, public
          // message from the server (429 capacity, 503 unconfigured, 502
          // upstream). Surface it verbatim instead of a generic stream error.
          if (!res.ok) {
            const body = await res.text().catch(() => "");
            throw new Error(publicErrorMessage(res.status, body));
          }
          // A stalled stream must fail visibly instead of leaving the visitor
          // on "Working…" forever.
          return withStallGuard(res);
        },
      }),
    [],
  );

  const { messages, setMessages, sendMessage, status, error, stop } = useChat({ transport });
  const busy = status === "submitted" || status === "streaming";
  const health = useMemo(
    () => connectionState({ busy, status, errorMessage: error?.message ?? null }),
    [busy, status, error],
  );

  // Restore local-only memory once, after hydration.
  useEffect(() => {
    const saved = loadLocalConversation();
    if (saved && saved.messages.length > 0) {
      setConversationId(saved.conversationId);
      setMessages(toUIMessages(saved.messages));
    } else {
      setConversationId(crypto.randomUUID());
    }
    setRestored(true);
  }, [setMessages]);

  // Persist locally on every settled change. Text only — no sources, no
  // metadata, no tokens, nothing that could carry private data.
  useEffect(() => {
    if (!restored || !conversationId || busy) return;
    if (messages.length === 0) return;
    saveLocalConversation({
      conversationId,
      messages: messages
        .filter((m) => m.role === "user" || m.role === "assistant")
        .map((m) => ({ id: m.id, role: m.role as "user" | "assistant", text: textOf(m) }))
        .filter((m) => m.text.length > 0),
    });
  }, [messages, restored, conversationId, busy]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, status]);

  // Auto-grow the composer within a bounded height (CSS caps it at max-h-40).
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [input]);

  /** Latest assistant answer, used only for browser-native read-aloud. */
  const lastAnswer = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i]!;
      if (m.role === "assistant") {
        const t = textOf(m);
        if (t.trim()) return t;
      }
    }
    return null;
  }, [messages]);


  /** Wipe the on-screen thread and everything stored in this browser. */
  const clearConversation = useCallback(() => {
    if (busy) stop();
    clearLocalConversation();
    setMessages([]);
    setRunId(null);
    setRated(null);
    setActiveRole(null);
    setInput("");
  }, [busy, stop, setMessages]);

  /** Clear, then start a fresh conversation id for telemetry continuity. */
  const newChat = useCallback(() => {
    clearConversation();
    setConversationId(crypto.randomUUID());
  }, [clearConversation]);

  const submit = useCallback(
    (text: string) => {
      const value = text.trim();
      if (!value || busy) return;
      setInput("");
      void sendMessage(
        { text: value },
        {
          body: {
            conversationId,
            role: roleChoice,
            // Only the validated `?from=` path is sent. The visitor's actual
            // location and document title are never transmitted.
            pagePath: contextPath ?? undefined,
            pageTitle: contextPath ? describePublicPath(contextPath) : undefined,
          },
        },
      );
    },
    [busy, conversationId, contextPath, roleChoice, sendMessage],
  );

  const rate = useCallback(
    async (value: 1 | -1) => {
      if (!runId) return;
      setRated(value);
      try {
        await rateIntelligenceRun({ data: { runId, rating: value } });
      } catch {
        /* rating is best-effort */
      }
    },
    [runId],
  );

  return (
    <main className="min-h-screen bg-background text-foreground">
      <div className="mx-auto flex min-h-screen w-full max-w-3xl flex-col px-4 pb-6 pt-10 sm:px-6">
        <header className="mb-6">
          <p className="mb-2 inline-flex items-center gap-2 rounded-full border border-gold/30 px-3 py-1 text-xs uppercase tracking-[0.2em] text-gold">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> Public beta
          </p>
          <h1 className="font-display text-3xl leading-tight sm:text-4xl">KA Intelligence</h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
            The reasoning system of KAfrequency, operating under the Eternal Buddha Mind
            constitution. It is software — not a teacher, not awakened, not infallible. It answers
            from governed site data where the question touches Buddha KA, the Codex, or the
            catalogue, and it tells you when it is interpreting rather than reporting.
          </p>
          <p className="mt-3 text-xs text-muted-foreground">
            Separate from{" "}
            <Link to="/ask" className="text-gold underline underline-offset-4">
              AI Buddha KA
            </Link>
            . Nothing here changes that experience.
          </p>
        </header>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <label htmlFor="ka-depth" className="text-xs uppercase tracking-wider text-muted-foreground">
            Depth
          </label>
          <select
            id="ka-depth"
            value={roleChoice}
            onChange={(e) => setRoleChoice(e.target.value)}
            className="rounded-md border border-border bg-card px-2 py-1 text-sm"
          >
            {ROLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {activeRole ? (
            <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
              answered as {ROLE_LABELS[activeRole as keyof typeof ROLE_LABELS] ?? activeRole}
            </span>
          ) : null}
          {contextPath ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-gold/30 px-2 py-0.5 text-xs text-gold">
              <MapPin className="h-3 w-3" aria-hidden="true" />
              Context: {describePublicPath(contextPath)}
            </span>
          ) : null}
          {messages.length > 0 ? (
            <span className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={clearConversation}
                className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground transition hover:border-gold/50 hover:text-gold"
              >
                <Trash2 className="h-3 w-3" aria-hidden="true" /> Clear conversation
              </button>
              <button
                type="button"
                onClick={newChat}
                className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground transition hover:border-gold/50 hover:text-gold"
              >
                <Plus className="h-3 w-3" aria-hidden="true" /> New chat
              </button>
            </span>
          ) : null}
        </div>

        <section aria-label="Conversation" className="flex-1">
          {messages.length === 0 ? (
            <div className="rounded-xl border border-border/60 bg-card/40 p-5">
              <h2 className="text-sm font-medium">Begin here</h2>
              <ul className="mt-3 grid gap-2">
                {OPENERS.map((o) => (
                  <li key={o}>
                    <button
                      type="button"
                      onClick={() => submit(o)}
                      className="w-full rounded-lg border border-border/60 px-3 py-2 text-left text-sm transition hover:border-gold/50 hover:text-gold"
                    >
                      {o}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <ol className="space-y-5">
              {messages.map((m) => {
                const body = textOf(m);
                return (
                  <li key={m.id} className={m.role === "user" ? "text-right" : ""}>
                    <div
                      className={
                        m.role === "user"
                          ? "inline-block max-w-[85%] rounded-2xl rounded-br-sm bg-secondary px-4 py-2.5 text-left text-sm"
                          : "rounded-2xl border border-border/50 bg-card/50 px-4 py-3"
                      }
                    >
                      {body ? (
                        <div className="prose prose-sm prose-invert max-w-none prose-a:text-gold">
                          <ReactMarkdown>{body}</ReactMarkdown>
                        </div>
                      ) : m.role === "assistant" && busy ? (
                        <p className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Working…
                        </p>
                      ) : null}
                      {m.role === "assistant" ? <SourceChips sources={sourcesOf(m)} /> : null}
                      {m.role === "assistant" && metaOf(m)?.action ? (
                        <ActionConfirmCard proposal={metaOf(m)!.action!} />
                      ) : null}
                      {m.role === "assistant" && metaOf(m)?.agent ? (
                        <AgentTraceSummary trace={metaOf(m)!.agent!} />
                      ) : null}
                    </div>
                  </li>
                );
              })}
              <div ref={bottomRef} />
            </ol>
          )}

          {error ? (
            <p
              role="alert"
              className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm"
            >
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{error.message || "Something interrupted the answer. Try again."}</span>
            </p>
          ) : null}

          {runId && !busy && messages.length > 0 ? (
            <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
              <span>Was this useful?</span>
              <button
                type="button"
                aria-label="Helpful"
                aria-pressed={rated === 1}
                onClick={() => void rate(1)}
                className={`rounded p-1 transition hover:text-gold ${rated === 1 ? "text-gold" : ""}`}
              >
                <ThumbsUp className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label="Not helpful"
                aria-pressed={rated === -1}
                onClick={() => void rate(-1)}
                className={`rounded p-1 transition hover:text-gold ${rated === -1 ? "text-gold" : ""}`}
              >
                <ThumbsDown className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          ) : null}
        </section>

        <form
          className={`sticky z-20 mt-6 bg-background/90 pb-2 pt-3 backdrop-blur ${CHAMBER_BOTTOM_RESERVE}`}
          onSubmit={(e) => {
            e.preventDefault();
            submit(input);
          }}
        >
          <div className="mb-2 flex items-center gap-2 text-[11px] text-muted-foreground">
            <span
              aria-hidden="true"
              className={`h-1.5 w-1.5 rounded-full ${
                health.tone === "ready"
                  ? "bg-emerald-400"
                  : health.tone === "working"
                    ? "animate-pulse bg-gold"
                    : "bg-destructive"
              }`}
            />
            <span role="status" aria-live="polite">
              {health.label}
            </span>
          </div>
          <label htmlFor="ka-input" className="sr-only">
            Ask KA Intelligence
          </label>
          <div className="flex items-end gap-2 rounded-2xl border border-border bg-card p-2">
            <textarea
              id="ka-input"
              ref={inputRef}
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submit(input);
                }
              }}
              placeholder="Ask anything — study, practice, catalogue, or craft."
              className="max-h-40 min-h-[2.5rem] flex-1 resize-none overflow-y-auto bg-transparent px-2 py-1.5 text-sm outline-none"
            />

            <VoiceControls
              onTranscript={(t) => setInput((prev) => (prev ? `${prev} ${t}` : t))}
              speakText={lastAnswer}
              disabled={busy}
            />


            {busy ? (
              <button
                type="button"
                onClick={stop}
                aria-label="Stop generating"
                className="rounded-xl border border-border p-2 hover:border-gold/60"
              >
                <Square className="h-4 w-4" aria-hidden="true" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={!input.trim()}
                aria-label="Send"
                className="rounded-xl bg-gold p-2 text-background transition disabled:opacity-40"
              >
                <Send className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>
          <p className="mt-2 text-center text-[11px] text-muted-foreground">
            Beta. KA Intelligence can be wrong. It is not medical, legal, or financial advice, and
            not a substitute for a human teacher or clinician. This conversation is stored only in
            your browser.
          </p>
        </form>
      </div>
    </main>
  );
}
